import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppNav from '../components/common/AppNav';
import { getPublicJourneys, type Journey, type JourneyStatus } from '../api/portalApi';
import { useCurrency } from '../contexts/CurrencyContext';
import '../styles/public.css';

const ACCENT = '#f59e0b';

function minPrice(j: Journey): { price: number; currency: string } | null {
  if (!j.durations?.length) return null;
  const sorted = [...j.durations].sort((a, b) => a.price - b.price);
  const first = sorted[0];
  if (!first) return null;
  return { price: first.price, currency: first.currency };
}

const JourneysPage: React.FC = () => {
  const { t } = useTranslation('landing');
  const { format: formatMoney } = useCurrency();
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState<JourneyStatus | 'all'>('active');

  useEffect(() => {
    getPublicJourneys()
      .then((r) => setJourneys(r.journeys))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const STATUS_LABELS: Record<JourneyStatus, { label: string; color: string; bg: string }> = {
    active: { label: t('journeysListPage.statusAvailable'), color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
    full:   { label: t('journeysListPage.statusFull'),      color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
    past:   { label: t('journeysListPage.statusPast'),      color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
    draft:  { label: t('journeysListPage.statusComingSoon'),color: '#38bdf8', bg: 'rgba(56,189,248,0.15)' },
  };

  const STATUS_ORDER: Record<string, number> = { active: 0, full: 1, draft: 2, past: 3 };

  const visible = filter === 'all'
    ? [...journeys].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9))
    : journeys.filter((j) => j.status === filter);

  const activeCount  = journeys.filter((j) => j.status === 'active').length;
  const totalCount   = journeys.length;

  return (
    <div className="journeys-page">
      <AppNav accentColor={ACCENT} />

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="journeys-page-hero">
        <p style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: ACCENT, margin: '0 0 14px' }}>
          {t('journeysListPage.eyebrow')}
        </p>
        <h1 className="journeys-page-title">{t('journeysListPage.title')}</h1>
        <p className="journeys-page-sub">
          {t('journeysListPage.sub')} {activeCount} active
          {totalCount > activeCount ? `, ${totalCount} total` : ''}.
        </p>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24, alignItems: 'center' }}>
          {(['active', 'full', 'all'] as const).map((f) => {
            const cfg = f === 'all' ? null : STATUS_LABELS[f];
            const isActive = filter === f;
            const label = f === 'all' ? t('journeysListPage.tabAll') : f === 'active' ? t('journeysListPage.tabAvailable') : t('journeysListPage.tabFull');
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '7px 18px',
                  borderRadius: 999,
                  border: `1px solid ${isActive ? (cfg?.color ?? ACCENT) : 'rgba(255,255,255,0.1)'}`,
                  background: isActive ? (cfg?.bg ?? 'rgba(245,158,11,0.15)') : 'transparent',
                  color: isActive ? (cfg?.color ?? ACCENT) : 'rgba(241,245,249,0.55)',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.18s',
                }}
              >
                {label}
              </button>
            );
          })}
          <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
          <button
            onClick={() => setFilter('past')}
            style={{
              padding: '5px 14px',
              borderRadius: 999,
              border: `1px solid ${filter === 'past' ? STATUS_LABELS.past.color : 'rgba(255,255,255,0.07)'}`,
              background: filter === 'past' ? STATUS_LABELS.past.bg : 'transparent',
              color: filter === 'past' ? STATUS_LABELS.past.color : 'rgba(241,245,249,0.3)',
              fontWeight: 600,
              fontSize: '0.74rem',
              cursor: 'pointer',
              transition: 'all 0.18s',
            }}
          >
            {t('journeysListPage.tabPast')}
          </button>
        </div>
      </div>

      {/* ── Grid ─────────────────────────────────────────── */}
      <div className="journeys-page-grid">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="journeys-page-skel" />
            ))
          : visible.length === 0
          ? (
              <div className="journeys-page-empty">
                <div className="journeys-page-empty-icon">✈️</div>
                <p>{t('journeysListPage.empty')}</p>
              </div>
            )
          : visible.map((j) => {
              const statusCfg = STATUS_LABELS[j.status] ?? STATUS_LABELS.active;
              const mp = minPrice(j);
              return (
                <Link
                  key={j._id}
                  to={`/journeys/${j._id}`}
                  className="journeys-page-card"
                >
                  <div className="journeys-page-cover">
                    {j.coverImageUrl ? (
                      <img src={j.coverImageUrl} alt={j.title} loading="lazy" />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', opacity: 0.3 }}>
                        ✈️
                      </div>
                    )}
                    <span
                      className="journeys-page-status"
                      style={{ background: statusCfg.bg, color: statusCfg.color }}
                    >
                      {statusCfg.label}
                    </span>
                    {j.spotsRemaining > 0 && j.status === 'active' && (
                      <span className="journeys-page-spots">
                        {t('journeysListPage.spotsLeft', { count: j.spotsRemaining })}
                      </span>
                    )}
                  </div>
                  <div className="journeys-page-body">
                    <p className="journeys-page-loc">{j.location}</p>
                    <p className="journeys-page-name">{j.title}</p>
                    <p className="journeys-page-excerpt">{j.tagline}</p>
                    <div className="journeys-page-footer">
                      {mp
                        ? <span className="journeys-page-price">{t('journeysListPage.from')} {formatMoney(mp.price, mp.currency)}</span>
                        : <span />
                      }
                      <span className="journeys-page-cta">{t('journeysListPage.view')} &rsaquo;</span>
                    </div>
                  </div>
                </Link>
              );
            })
        }
      </div>
    </div>
  );
};

export default JourneysPage;
